/* global process:true document:true*/
/*eslint no-undef: "error"*/

import reduceReducers from 'reduce-reducers'
import thunk from 'redux-thunk'
import { combineReducers, applyMiddleware, createStore, compose } from 'redux'
import createSagaMiddleware from 'redux-saga'
import { all, fork } from 'redux-saga/effects'
import { injectorSaga } from 'redux-saga-injector'
import { createInjectStore } from 'helpers/reedux'
import { ENV } from 'config.js'
import initializeState from 'initialState'

import { batchedSubscribe } from 'redux-batched-subscribe'
import {debounce} from 'lodash'

/* ---------------- Reducers --------------------------- */
import report from 'containers/Report/reducer'

import redirect from 'containers/Redirect/reducer'
import {
  user,
  employeeListReducer as employeeList,
  employeeImageReducer as employeeImages,
  substitutesReducer as substitutes,
  addNewSubstituteReducer as newSubstitute,
} from 'containers/User/reducer'
import asyncActions from 'containers/Async/reducer'
import uiState from 'containers/UiState/reducer'
import {
  entityReducer as entities,
  resultReducer as results,
  rootEntityReducer
} from 'containers/Entity/reducer'
import {
  projectCreateReducer as projectCreate
} from 'containers/ProjectCreatePage/reducer'
import {
  projectOverviewReducer as projectOverview
} from 'containers/Project/Overview/reducer'
import {
  productPgmOverviewReducer as productPgm
} from 'containers/ProductPgmOverview/reducer'
import product from 'containers/Product/reducer'
import {
  formReducer as form,
  validationReducer as formValidation,
  riskValidationReducer as riskValidation
} from 'containers/Form/reducer'
import projectForm from 'containers/Form/Project/reducer'
import {
  projectReducer as project,
} from 'containers/Project/reducer'

import {
  default as filePreview
} from 'containers/Project/FilePreview/reducer'

import approvals from 'containers/Approval/reducer'
import admin from 'containers/Admin/reducer'
import manage from 'containers/Manage/reducer'
import { logReducer } from 'helpers/ReducerUtil'
import roles from 'containers/Role/reducer'
import app from 'containers/App/reducer'
import revisions from 'containers/Project/Revision/reducer'
import { myProjectsReducer as myProjects } from 'containers/MyProjects/reducer'

/* ---------------- Sagas --------------------------- */

import {
  watchMount as watchWeeklyStatusReportMount
} from 'containers/Report/WeeklyStatus/sagas'

import {
  watchMount as watchGreenlightReportMount
} from 'containers/Report/Greenlight/sagas'

import {
  watchMount as watchDashboardMount
} from 'containers/Report/Dashboard/sagas'

import {
  watchMount as watchNvqualOverviewMount
} from 'containers/Report/Nvqual/sagas'

import {
  watchUpdateTables,
  watchRemoveTables,
  watchRemoveRow,
  watchOverwriteTables
} from 'containers/Entity/sagas'
import {
  watchLoadApprovalPage,
  watchFetchApprovalCount,
} from 'containers/Approval/ApprovalPage/sagas'
import {
  watchAsync
} from 'containers/Async/sagas'
import formSagas from 'containers/Form/sagas'
import {
  loadSagas as watchUpdateProject,
} from 'containers/Project/sagas'
import {
  loadSagas as watchProjectSearchMount
} from 'containers/Project/ElasticSearch/sagas'
import {
  watchGetEmployeeList,
  watchFetchEmployeeImage,
  watchFetchEmployeeListWithImages,
  watchLoginPage,
} from 'containers/User/sagas'
import {
  logSagas
} from 'helpers/SagaUtil'
import roleSagas from 'containers/Role/sagas'
import NotificationSagas from 'containers/App/Notification/sagas'
import scheduleSagas from 'containers/Project/ScheduleSection/sagas'
import hulkRequestSaga from 'containers/Project/HulkRequest/sagas'

/* ----------------------------------------------- */

const initialState = initializeState()
const sagaMiddleware = createSagaMiddleware()

const composeEnhancers = (
  process.env.NODE_ENV === 'production' ? compose :
  window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ :
  compose
)

function configureStore() {
  const middlewares = [
    thunk,
    sagaMiddleware,
  ]

  const enhancers = [
    applyMiddleware(...middlewares),
    batchedSubscribe(debounce(
      notify => {notify()}
    ))
  ]

  const combinedReducers = combineReducers({
    app,
    admin,
    manage,
    user,
    newSubstitute,
    substitutes,
    employeeList,
    employeeImages,
    projectCreate,
    asyncActions,
    entities,
    results,
    projectOverview,
    uiState,
    myProjects,
    productPgm,
    product,
    project,
    projectForm,
    form,
    formValidation,
    filePreview,
    redirect,
    revisions,
    riskValidation,
    approvals,
    report,
    roles
  })

  const globalReducers = [
    combinedReducers,
    rootEntityReducer,
  ]

  const sagas = [
    watchUpdateTables,
    watchOverwriteTables,
    watchRemoveTables,
    watchRemoveRow,
    watchFetchApprovalCount,
    watchLoadApprovalPage,
    watchAsync,
    watchUpdateProject,
    watchGetEmployeeList,
    watchFetchEmployeeImage,
    watchFetchEmployeeListWithImages,
    watchGreenlightReportMount,
    watchWeeklyStatusReportMount,
    watchDashboardMount,
    watchNvqualOverviewMount,
    watchLoginPage,
    watchProjectSearchMount,
    formSagas,
    roleSagas,
    NotificationSagas,
    scheduleSagas,
    hulkRequestSaga
  ]

  if(ENV !== 'production') {
    sagas.push(logSagas)
  }

  const combinedSagas = function*() {
    yield all(sagas.map(saga => fork(saga)))
  }

  const reducers = reduceReducers(...globalReducers)

  const store = createStore(
    reducers,
    initialState,
    composeEnhancers(...enhancers)
  )

  sagaMiddleware.run(injectorSaga)
  sagaMiddleware.run(combinedSagas)

  // Used for injecting future reducers
  createInjectStore(store, reducers)
  return store
}

export default configureStore()